<template>
    <div class="student-notif-panel" role="region" aria-label="Painel de notificações">
        <header class="student-notif-panel__head">
            <div class="student-notif-panel__head-row">
                <span class="student-notif-panel__icon" aria-hidden="true">
                    <fa icon="bell" />
                </span>
                <div class="student-notif-panel__head-text">
                    <h2 class="student-notif-panel__title">{{ title }}</h2>
                    <p
                        v-if="unreadTotal > 0"
                        class="student-notif-panel__subtitle"
                    >
                        {{ unreadTotal }}
                        {{ unreadTotal === 1 ? 'não lida' : 'não lidas' }}
                    </p>
                    <p v-else class="student-notif-panel__subtitle">
                        Você está em dia
                    </p>
                </div>
            </div>
        </header>

        <div class="student-notif-panel__body">
            <ul
                v-if="limitedNotifications.length > 0"
                class="student-notif-panel__list"
                role="list"
            >
                <li
                    v-for="notification in limitedNotifications"
                    :key="notification.id"
                    class="student-notif-panel__item"
                    :class="{ 'student-notif-panel__item--unread': notification.isRead == 0 }"
                    role="listitem"
                    tabindex="0"
                    @click="redirectToInquiries"
                    @keydown.enter="redirectToInquiries"
                >
                    <div class="student-notif-panel__item-main">
                        <span class="student-notif-panel__item-title">
                            {{ notification.title }}
                        </span>
                        <span
                            v-if="notification.isRead == 0"
                            class="student-notif-panel__dot"
                            aria-label="Não lida"
                        />
                    </div>
                    <div
                        class="student-notif-panel__item-desc"
                        v-html="textoComEllipsis(notification.description)"
                    />
                </li>
            </ul>

            <div
                v-else
                class="student-notif-panel__empty"
                role="status"
            >
                <span class="student-notif-panel__empty-icon" aria-hidden="true">
                    <fa icon="inbox" />
                </span>
                <p class="student-notif-panel__empty-title">
                    Nenhuma notificação não lida
                </p>
                <p class="student-notif-panel__empty-hint">
                    Quando houver novidades, elas aparecem aqui.
                </p>
            </div>
        </div>

        <footer class="student-notif-panel__foot">
            <button
                type="button"
                class="student-notif-panel__cta"
                @click.stop="redirectToInquiries"
            >
                Ver todas as notificações
            </button>
        </footer>
    </div>
</template>

<script>
import api from '@/utils/portal/notification.js';
import { mapState } from 'vuex';

export default {
    props: {
        title: {
            type: String,
            required: true,
        },
        data: {
            type: Array,
            required: true,
        },
    },
    computed: {
        ...mapState(['token']),
        limitedNotifications() {
            if (!this.data || this.data.length === 0) {
                return [];
            }
            const unread = this.data.filter((n) => n.isRead === 0);
            return unread.slice(0, 2);
        },
        unreadTotal() {
            if (!this.data || !this.data.length) return 0;
            return this.data.filter((n) => n.isRead === 0).length;
        },
    },
    methods: {
        redirectToInquiries() {
            window.location = '/aluno/notificacoes';
        },
        async updateUnreadCount() {
            try {
                const response = await api.getNumberNotifications(this.token);
                this.$emit('unread-count-updated', response);
            } catch (error) {
                console.error('Erro ao atualizar contador de notificações:', error);
            }
        },
        textoComEllipsis(text) {
            if (!text) return '';
            if (text.length > 80) {
                return text.substring(0, 80) + '...';
            }
            return text;
        },
    },
    mounted() {
        this.updateUnreadCount();
        window.addEventListener('notification-read', this.updateUnreadCount);
    },
    beforeUnmount() {
        window.removeEventListener('notification-read', this.updateUnreadCount);
    },
};
</script>

<style scoped>
.student-notif-panel {
    flex: 1 1 auto;
    min-height: 0;
    --notif-accent: #19c7d0;
    --notif-accent-soft: rgba(25, 199, 208, 0.12);
    --notif-border: #e8eaf0;
    --notif-bg: #ffffff;
    --notif-bg-muted: #f8fafc;
    --notif-text: #111827;
    --notif-text-muted: #6b7280;
    --notif-radius: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: inherit;
    text-align: left;
    font-family: inherit;
}

.student-notif-panel__head {
    flex-shrink: 0;
    padding: 14px 16px;
    background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
    border-bottom: 1px solid var(--notif-border);
}

.student-notif-panel__head-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.student-notif-panel__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--notif-accent-soft);
    color: #0f766e;
    font-size: 1.1rem;
    flex-shrink: 0;
}

.student-notif-panel__head-text {
    min-width: 0;
}

.student-notif-panel__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--notif-text);
    letter-spacing: -0.02em;
    line-height: 1.25;
}

.student-notif-panel__subtitle {
    margin: 4px 0 0;
    font-size: 0.8125rem;
    color: var(--notif-text-muted);
    line-height: 1.35;
}

.student-notif-panel__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.student-notif-panel__list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.student-notif-panel__item {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    transition: background 0.18s ease;
}

.student-notif-panel__item:hover,
.student-notif-panel__item:focus {
    outline: none;
    background: rgba(25, 199, 208, 0.06);
}

.student-notif-panel__item--unread {
    background: rgba(25, 199, 208, 0.04);
}

.student-notif-panel__item-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.student-notif-panel__item-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--notif-text);
    line-height: 1.3;
}

.student-notif-panel__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--notif-accent);
    flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(25, 199, 208, 0.25);
}

.student-notif-panel__item-desc {
    margin-top: 6px;
    font-size: 0.8125rem;
    color: var(--notif-text-muted);
    line-height: 1.45;
    word-break: break-word;
}

.student-notif-panel__empty {
    padding: 28px 18px 32px;
    text-align: center;
}

.student-notif-panel__empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    margin-bottom: 12px;
    border-radius: 14px;
    background: #f1f5f9;
    color: #94a3b8;
    font-size: 1.35rem;
}

.student-notif-panel__empty-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--notif-text-muted);
}

.student-notif-panel__empty-hint {
    margin: 8px 0 0;
    font-size: 0.8125rem;
    color: #94a3b8;
    line-height: 1.4;
}

.student-notif-panel__foot {
    flex-shrink: 0;
    padding: 12px 14px 14px;
    border-top: 1px solid var(--notif-border);
    background: var(--notif-bg-muted);
}

.student-notif-panel__cta {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 14px;
    border: 1px solid rgba(25, 199, 208, 0.45);
    border-radius: 10px;
    background: #fff;
    color: #0d9488;
    font-size: 0.875rem;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.18s ease,
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.student-notif-panel__cta:hover {
    background: rgba(25, 199, 208, 0.08);
    border-color: var(--notif-accent);
    box-shadow: 0 2px 10px rgba(25, 199, 208, 0.2);
}
</style>

<style>
/* Painel dentro do dropdown do header (pai `.notification_bar` está em Header.vue) */
.notification_bar.dark .student-notif-panel {
    --notif-border: #353945;
    --notif-bg: #1a191f;
    --notif-bg-muted: #141318;
    --notif-text: #f1f5f9;
    --notif-text-muted: #9ca3af;
}

.notification_bar.dark .student-notif-panel__head {
    background: linear-gradient(180deg, #22252d 0%, #1a191f 100%);
    border-bottom-color: #353945;
}

.notification_bar.dark .student-notif-panel__icon {
    background: rgba(25, 199, 208, 0.15);
    color: #5eead4;
}

.notification_bar.dark .student-notif-panel__item {
    border-bottom-color: #2d313c;
}

.notification_bar.dark .student-notif-panel__item:hover,
.notification_bar.dark .student-notif-panel__item:focus {
    background: rgba(109, 90, 205, 0.12);
}

.notification_bar.dark .student-notif-panel__item--unread {
    background: rgba(25, 199, 208, 0.08);
}

.notification_bar.dark .student-notif-panel__empty-icon {
    background: #252730;
    color: #6b7280;
}

.notification_bar.dark .student-notif-panel__cta {
    background: #252730;
    color: #5eead4;
    border-color: rgba(25, 199, 208, 0.35);
}

.notification_bar.dark .student-notif-panel__cta:hover {
    background: rgba(25, 199, 208, 0.12);
    border-color: #19c7d0;
}
</style>
